import {
	OrderSampleChoice,
	useConfirmPendingOfferAcceptanceMutation,
	useGetOrderQuoteOfferBatchesQuery,
	useRejectPendingOfferAcceptanceMutation,
	useReviseLateOfferAcceptanceMutation,
} from '@codezo/graphql';
import { Check, Clock, Pencil, X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CurrencyText } from '../CurrencyText';
import { useCurrencyStore } from '../../stores/currency';
import { SHIPPING_METHOD_LABEL } from '../../lib/status-configs';
import { formatDate } from '../../lib/utils';
import { Button } from '../ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../ui/dialog';
import { Field } from '../ui/field';
import { Textarea } from '../ui/textarea';

/* ── pendingAcceptance JSON shape (kept narrow — the field is a JSON scalar) ── */

interface PendingSelection {
	offerId: string;
	pricingId: string;
	quantity: number;
	deliveryId?: string | null;
	sampleChoice?: OrderSampleChoice | null;
}

interface PendingAcceptancePayload {
	selections?: PendingSelection[];
	acceptedByRole?: string | null;
	acceptedAt?: string | null;
}

interface SupersededAcceptance {
	selections?: PendingSelection[];
	acceptedByRole?: string | null;
	acceptedAt?: string | null;
	missedDeadline?: string | null;
	revisedByRole?: string | null;
	revisedAt?: string | null;
}

interface QuoteItem {
	id: string;
	productName?: string | null;
}

interface PendingAcceptanceReviewProps {
	quoteId: string;
	quoteItems: QuoteItem[];
	/** Raw `OrderQuote.pendingAcceptance` JSON value. */
	pendingAcceptance: unknown;
	/** Raw `OrderQuote.supersededAcceptances` JSON value (append-only revise history). */
	supersededAcceptances?: unknown;
	/** Refetch the quote after reject/revise (confirm navigates away to the new order). */
	onResolved: () => void;
	/** Scroll to the offers section after a revise so the admin can edit + re-send. */
	onRevise?: () => void;
}

const SAMPLE_CHOICE_LABEL: Record<OrderSampleChoice, string> = {
	[OrderSampleChoice.NoSample]: 'No sample',
	[OrderSampleChoice.SampleNoShipping]: 'Sample — video/images',
	[OrderSampleChoice.SampleWithShipping]: 'Sample — physical delivery',
};

export function PendingAcceptanceReview({
	quoteId,
	quoteItems,
	pendingAcceptance,
	supersededAcceptances,
	onResolved,
	onRevise,
}: PendingAcceptanceReviewProps) {
	const navigate = useNavigate();
	const { selectedCurrency } = useCurrencyStore();
	const [rejectOpen, setRejectOpen] = useState(false);
	const [rejectReason, setRejectReason] = useState('');

	/* Resolving a late acceptance settles the batch as well as the quote — it is
	   accepted, sent back, or reopened for a revision — and the offers section
	   owns that query, so it is invalidated by name rather than through
	   `onResolved`, which only reaches the page. */
	const refetchQueries = ['GetOrderQuoteOfferBatches'];
	const [confirmAcceptance, { loading: confirming }] = useConfirmPendingOfferAcceptanceMutation({
		refetchQueries,
	});
	const [rejectAcceptance, { loading: rejecting }] = useRejectPendingOfferAcceptanceMutation({
		refetchQueries,
	});
	const [reviseAcceptance, { loading: revising }] = useReviseLateOfferAcceptanceMutation({
		refetchQueries,
	});
	const busy = confirming || rejecting || revising;

	// Resolve offer/pricing/delivery names against the live offer batches.
	const { data: batchData } = useGetOrderQuoteOfferBatchesQuery({
		variables: { orderQuoteId: quoteId, inCurrency: selectedCurrency },
		fetchPolicy: 'no-cache',
	});
	const offers = (batchData?.getOrderQuoteOfferBatches ?? []).flatMap((b) => b.offers);
	const itemName = new Map(quoteItems.map((i) => [i.id, i.productName]));

	const payload = (pendingAcceptance ?? {}) as PendingAcceptancePayload;
	const selections = payload.selections ?? [];
	const superseded = (
		Array.isArray(supersededAcceptances) ? supersededAcceptances : []
	) as SupersededAcceptance[];

	const handleConfirm = async () => {
		try {
			const { data } = await confirmAcceptance({ variables: { orderQuoteId: quoteId } });
			const res = data?.confirmPendingOfferAcceptance;
			if (res?.success) {
				toast.success(res.message || 'Acceptance confirmed — order created');
				if (res.orderId) {
					navigate(`/orders/${res.orderId}`);
				} else {
					onResolved();
				}
			} else {
				toast.error(res?.message || 'Failed to confirm acceptance');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to confirm acceptance');
		}
	};

	const handleRevise = async () => {
		try {
			const { data } = await reviseAcceptance({ variables: { orderQuoteId: quoteId } });
			const res = data?.reviseLateOfferAcceptance;
			if (res?.success) {
				toast.success(
					res.message || 'Offer reopened — edit the terms and re-send to the client'
				);
				onResolved();
				onRevise?.();
			} else {
				toast.error(res?.message || 'Failed to reopen the offer');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to reopen the offer');
		}
	};

	const handleReject = async () => {
		try {
			const { data } = await rejectAcceptance({
				variables: { orderQuoteId: quoteId, reason: rejectReason.trim() || undefined },
			});
			const res = data?.rejectPendingOfferAcceptance;
			if (res?.success) {
				toast.success(res.message || 'Acceptance rejected');
				setRejectOpen(false);
				setRejectReason('');
				onResolved();
			} else {
				toast.error(res?.message || 'Failed to reject acceptance');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to reject acceptance');
		}
	};

	return (
		<section className="overflow-hidden rounded-[var(--r-3)] border border-status-warn/30 bg-status-warn/5">
			{/* Header */}
			<div className="flex items-start gap-3 border-b border-status-warn/20 px-5 py-4">
				<Clock size={18} className="mt-0.5 shrink-0 text-status-warn" />
				<div className="min-w-0 flex-1">
					<div className="text-base font-medium text-text-1">
						Acceptance awaiting your confirmation
					</div>
					<div className="mt-0.5 text-sm leading-relaxed text-text-3">
						The client accepted after the offer’s acceptance window had passed. No order
						was created. <strong>Confirm</strong> to create the order at the current
						terms, <strong>revise</strong> to reopen the offer for editing and re-send
						(the client then re-accepts), or <strong>reject</strong> to expire the
						quote.
					</div>
					<div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-text-4">
						{payload.acceptedByRole && (
							<span>Accepted by {payload.acceptedByRole.toLowerCase()}</span>
						)}
						{payload.acceptedAt && <span>{formatDate(payload.acceptedAt)}</span>}
					</div>
				</div>
			</div>

			{/* Selected products */}
			{selections.length > 0 && (
				<div className="flex flex-col gap-1.5 px-5 py-4">
					<div className="mb-1 text-2xs font-medium uppercase tracking-[0.08em] text-text-4">
						Client selection
					</div>
					{selections.map((sel, i) => {
						const offer = offers.find((o) => o.id === sel.offerId);
						const pricing = offer?.pricing?.find((p) => p.id === sel.pricingId);
						const delivery = pricing?.deliveries?.find((d) => d.id === sel.deliveryId);
						const name =
							(offer && itemName.get(offer.orderQuoteItemId)) ||
							offer?.name ||
							'Product';
						return (
							<div
								key={`${sel.offerId}-${i}`}
								className="flex items-center gap-3 rounded-lg border border-line-1 bg-surface-sunken px-3 py-2.5"
							>
								<div className="min-w-0 flex-1">
									<div className="text-sm font-medium text-text-1">{name}</div>
									<div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-4">
										<span>{sel.quantity.toLocaleString()} pcs</span>
										{delivery && (
											<>
												<span>·</span>
												<span>
													{SHIPPING_METHOD_LABEL[delivery.shippingMethod]}
												</span>
											</>
										)}
										{sel.sampleChoice && (
											<>
												<span>·</span>
												<span>{SAMPLE_CHOICE_LABEL[sel.sampleChoice]}</span>
											</>
										)}
									</div>
								</div>
								{pricing?.totalPrice != null && (
									<CurrencyText
										amount={pricing.totalPrice}
										className="text-sm font-medium text-text-1"
									/>
								)}
							</div>
						);
					})}
				</div>
			)}

			{/* Prior revise rounds (append-only audit history) */}
			{superseded.length > 0 && (
				<div className="border-t border-status-warn/15 px-5 py-4">
					<div className="mb-1.5 text-2xs font-medium uppercase tracking-[0.08em] text-text-4">
						Previously revised ({superseded.length})
					</div>
					<div className="flex flex-col gap-1">
						{superseded.map((s, i) => (
							<div
								key={i}
								className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-4"
							>
								<span className="text-text-3">
									{(s.selections?.length ?? 0).toLocaleString()} item
									{(s.selections?.length ?? 0) !== 1 ? 's' : ''}
								</span>
								{s.acceptedAt && <span>· accepted {formatDate(s.acceptedAt)}</span>}
								{s.missedDeadline && (
									<span>· missed {formatDate(s.missedDeadline)}</span>
								)}
								{s.revisedAt && <span>· revised {formatDate(s.revisedAt)}</span>}
							</div>
						))}
					</div>
				</div>
			)}

			{/* Actions */}
			<div className="flex flex-wrap items-center justify-end gap-2 border-t border-status-warn/20 px-5 py-3">
				<Button variant="outline" size="sm" onClick={handleRevise} disabled={busy}>
					<Pencil size={14} data-icon="inline-start" />
					{revising ? 'Reopening…' : 'Revise & edit'}
				</Button>
				<Button
					variant="outline"
					size="sm"
					onClick={() => setRejectOpen(true)}
					disabled={busy}
				>
					<X size={14} data-icon="inline-start" />
					Reject
				</Button>
				<Button size="sm" onClick={handleConfirm} disabled={busy}>
					<Check size={14} data-icon="inline-start" />
					{confirming ? 'Confirming…' : 'Confirm & create order'}
				</Button>
			</div>

			{/* Reject dialog */}
			<Dialog
				open={rejectOpen}
				onOpenChange={(open) => {
					if (!open) {
						setRejectOpen(false);
						setRejectReason('');
					}
				}}
			>
				<DialogContent className="sm:max-w-sm">
					<DialogForm onSubmit={handleReject}>
						<DialogHeader className="p-4">
							<DialogTitle>Reject acceptance</DialogTitle>
							<DialogDescription>
								The quote will be expired and the client’s captured selections
								cleared. This cannot be undone.
							</DialogDescription>
						</DialogHeader>
						<DialogBody>
							<Field label="Reason">
								<Textarea
									value={rejectReason}
									onChange={(e) => setRejectReason(e.target.value)}
									placeholder="Optional — shared context for the audit trail"
									rows={3}
									autoFocus
								/>
							</Field>
						</DialogBody>
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => {
									setRejectOpen(false);
									setRejectReason('');
								}}
							>
								Cancel
							</Button>
							<Button type="submit" variant="destructive" disabled={rejecting}>
								{rejecting ? 'Rejecting…' : 'Reject acceptance'}
							</Button>
						</DialogFooter>
					</DialogForm>
				</DialogContent>
			</Dialog>
		</section>
	);
}
